import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { toastError } from '../../components/ui/Toast.js';

/**
 * API keys for one customer.
 *
 * Shaped around the two facts that make a key different from a login: the
 * secret is visible exactly once, and there is nobody to ask when it turns up
 * somewhere unexpected. So creating one is a deliberate act with an explicit
 * permission list, and every key on the page can be revoked on its own.
 */

interface ApiKeyRow {
  id: string;
  key_id: string;
  name: string;
  scopes: string[];
  ip_allowlist: string[] | null;
  rate_limit_rpm: number;
  expires_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface Grantable { key: string; description: string }

export function ApiKeysTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const writable = can('apikey:write');
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ name: string; secret: string; key_id: string } | null>(null);

  const key = ['tenants', tenantId, 'api-keys'] as const;
  const keys = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ data: ApiKeyRow[]; enabled: boolean }>(
      `/v1/tenants/${tenantId}/api-keys`),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/api-keys/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  // apikey:write is a step-up permission: the API asks for the password again (permission sweep,
  // 19 September 2026). Without this the refusal surfaced as an error with no way through.
  const step = useStepUp();

  if (keys.error) return <ErrorBanner error={keys.error} />;

  const rows = keys.data?.data ?? [];
  const live = rows.filter((k) => !k.revoked_at);
  const dead = rows.filter((k) => k.revoked_at);

  return (
    <>
      <StepUpPrompt step={step} what="Revoking an API key" />
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">API keys</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Credentials for machines. Each one carries its own permissions and
            can be revoked without affecting the others.
          </p>
        </div>
        {writable && !adding && !issued && (
          <button type="button" className="arc-btn arc-btn-primary" onClick={() => setAdding(true)}>
            <Icon name="plus" size={13} /> New key
          </button>
        )}
      </div>

      {/*
        Stated plainly rather than left to be discovered. Keys can be issued and
        revoked while the API is closed -- what they cannot do yet is
        authenticate, and finding that out by debugging a 401 would be a bad
        afternoon.
      */}
      {keys.data && !keys.data.enabled && (
        <div className="arc-banner arc-banner-info" style={{ marginBottom: 14 }}>
          <Icon name="info" size={14} />
          <span>
            The public API is not open on this deployment yet, so keys issued
            here can be managed but will be refused if used. Everything else on
            this page works normally.
          </span>
        </div>
      )}

      {issued && (
        <IssuedKey issued={issued} onDone={() => { setIssued(null); void keys.refetch(); }} />
      )}

      {adding && !issued && (
        <NewKeyForm
          tenantId={tenantId}
          onCancel={() => setAdding(false)}
          onCreated={(k) => { setAdding(false); setIssued(k); }}
        />
      )}

      <div className="arc-card">
        {keys.isLoading ? (
          <TableSkeleton rows={4} cols={5} />
        ) : rows.length === 0 ? (
          <Empty
            icon="settings"
            title="No API keys"
            hint="A key lets an integration act on this account without a person logging in — a CRM reading call history, or a dialler creating calls."
          />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Name</th><th>Key ID</th><th>Permissions</th>
                  <th>Restrictions</th><th>Last used</th>
                  {writable && <th style={{ width: 60 }} />}
                </tr>
              </thead>
              <tbody>
                {[...live, ...dead].map((k) => (
                  <tr key={k.id} style={k.revoked_at ? { opacity: 0.55 } : undefined}>
                    <td>
                      <div className="arc-row" style={{ gap: 6 }}>
                        <span style={{ fontWeight: 550 }}>{k.name}</span>
                        {k.revoked_at && <Badge tone="danger">revoked</Badge>}
                        {!k.revoked_at && k.expires_at
                          && new Date(k.expires_at).getTime() < Date.now()
                          && <Badge tone="warn">expired</Badge>}
                      </div>
                      <div className="arc-subtle" style={{ marginTop: 2 }}>
                        created {relativeTime(k.created_at)}
                        {k.expires_at && <> · expires {new Date(k.expires_at).toLocaleDateString('en-ZA')}</>}
                      </div>
                    </td>
                    <td className="arc-mono">{k.key_id}</td>
                    <td>
                      <div className="arc-row" style={{ gap: 4, flexWrap: 'wrap', maxWidth: 320 }}>
                        {k.scopes.slice(0, 4).map((s) => (
                          <span key={s} className="arc-badge arc-badge-muted arc-mono">{s}</span>
                        ))}
                        {k.scopes.length > 4 && (
                          <span className="arc-subtle">+{k.scopes.length - 4} more</span>
                        )}
                      </div>
                    </td>
                    <td className="arc-subtle">
                      {k.ip_allowlist && k.ip_allowlist.length > 0
                        ? <span className="arc-mono">{k.ip_allowlist.join(', ')}</span>
                        : 'any address'}
                      <div style={{ marginTop: 2 }}>{k.rate_limit_rpm}/min</div>
                    </td>
                    <td className="arc-subtle">
                      {k.last_used_at
                        ? <>{relativeTime(k.last_used_at)}
                            {k.last_used_ip && <div className="arc-mono">{k.last_used_ip}</div>}</>
                        : 'never'}
                    </td>
                    {writable && (
                      <td>
                        {!k.revoked_at && (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            title="Revoke this key" onClick={() => { step.run(() => revoke.mutateAsync(k.id)).catch(toastError); }}>
                            <Icon name="trash" size={12} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Shown once, in place of everything else.
 *
 * The same reasoning as the SIP password on a new extension: this is the only
 * moment the secret exists outside a hash, and a panel that can be dismissed by
 * accident takes the integration's credential with it.
 */
function IssuedKey({ issued, onDone }: {
  issued: { name: string; secret: string; key_id: string }; onDone: () => void;
}) {
  return (
    <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
      <h2 className="arc-h2" style={{ marginBottom: 4 }}>{issued.name} created</h2>
      <div className="arc-banner arc-banner-warn" style={{ margin: '10px 0 14px' }}>
        <Icon name="warning" size={14} />
        <span>
          This is the only time the secret is shown. Only a hash of it is stored,
          so it cannot be recovered — if it is lost the key has to be revoked and
          replaced.
        </span>
      </div>
      <dl className="arc-kv">
        <dt>Key ID</dt><dd className="arc-mono">{issued.key_id}</dd>
        <dt>Secret</dt>
        <dd className="arc-row" style={{ gap: 8 }}>
          <span className="arc-mono" style={{ fontWeight: 600, userSelect: 'all', wordBreak: 'break-all' }}>
            {issued.secret}
          </span>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
            onClick={() => void navigator.clipboard?.writeText(issued.secret)}>
            Copy
          </button>
        </dd>
      </dl>
      <p className="arc-hint" style={{ marginTop: 10 }}>
        Send it as <span className="arc-mono">Authorization: Bearer &lt;secret&gt;</span>.
      </p>
      <button type="button" className="arc-btn arc-btn-primary" style={{ marginTop: 12 }}
        onClick={onDone}>
        I have saved it
      </button>
    </div>
  );
}

function NewKeyForm({ tenantId, onCancel, onCreated }: {
  tenantId: string;
  onCancel: () => void;
  onCreated: (k: { name: string; secret: string; key_id: string }) => void;
}) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [allowlist, setAllowlist] = useState('');
  const [expiryDays, setExpiryDays] = useState('365');

  const grantable = useQuery({
    queryKey: ['tenants', tenantId, 'api-keys', 'grantable'] as const,
    queryFn: () => api.get<{ data: Grantable[] }>(
      `/v1/tenants/${tenantId}/api-keys/grantable`),
  });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ key_id: string; secret: string }>(`/v1/tenants/${tenantId}/api-keys`, body),
    onSuccess: (r) => onCreated({ name, secret: r.secret, key_id: r.key_id }),
  });
  const step = useStepUp();

  /** Grouped by resource, since a caller thinks "what may it do to extensions?". */
  const groups = (grantable.data?.data ?? []).reduce<Record<string, Grantable[]>>((acc, g) => {
    const resource = g.key.split(':')[0] ?? 'other';
    (acc[resource] ??= []).push(g);
    return acc;
  }, {});

  const toggle = (k: string) =>
    setScopes((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <div className="arc-card-head"><h2 className="arc-h2">New API key</h2></div>
      <div className="arc-card-body">
        <StepUpPrompt step={step} what="Creating an API key" />
        <div className="arc-grid arc-grid-2" style={{ gap: 10 }}>
          <label className="arc-field">
            <span>Name</span>
            <input className="arc-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="CRM integration" />
            <div className="arc-hint">What holds it. Used when deciding whether to revoke.</div>
          </label>
          <label className="arc-field">
            <span>Expires after</span>
            <select className="arc-select" value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="365">1 year</option>
              <option value="">Never</option>
            </select>
            <div className="arc-hint">
              {expiryDays
                ? 'A key that expires is one that cannot be forgotten about indefinitely.'
                : 'No expiry — this key stays valid until somebody revokes it.'}
            </div>
          </label>
        </div>

        <label className="arc-field" style={{ marginTop: 10 }}>
          <span>Allowed addresses</span>
          <input className="arc-input arc-mono" value={allowlist}
            onChange={(e) => setAllowlist(e.target.value)}
            placeholder="196.25.0.0/16, 41.0.0.1" />
          <div className="arc-hint">
            Comma separated. Exact addresses or IPv4 CIDR blocks; IPv6 must be
            exact. Leave empty to accept any address.
          </div>
        </label>

        <div className="arc-field" style={{ marginTop: 14 }}>
          <span className="arc-label">Permissions</span>
          <div className="arc-hint" style={{ marginBottom: 8 }}>
            Only what you hold yourself is offered, and permissions that issue or
            reveal credentials are never available to a key. Grant the least that
            works — this is the whole of what a leaked key could do.
          </div>
          {grantable.isLoading ? (
            <TableSkeleton rows={3} cols={2} />
          ) : (
            <div className="arc-stack" style={{ gap: 10, maxHeight: 300, overflowY: 'auto' }}>
              {Object.entries(groups).map(([resource, list]) => (
                <div key={resource}>
                  <div className="arc-label arc-mono" style={{ marginBottom: 4 }}>{resource}</div>
                  <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {list.map((g) => (
                      <label key={g.key} className="arc-row" title={g.description}
                        style={{
                          gap: 5, padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
                          border: '1px solid var(--arc-border)',
                          background: scopes.includes(g.key) ? 'var(--arc-accent-quiet)' : undefined,
                        }}>
                        <input type="checkbox" checked={scopes.includes(g.key)}
                          onChange={() => toggle(g.key)} />
                        <span className="arc-mono" style={{ fontSize: 11.5 }}>{g.key}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <span className="arc-subtle" style={{ marginRight: 'auto' }}>
            {scopes.length} permission{scopes.length === 1 ? '' : 's'} selected
          </span>
          <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="arc-btn arc-btn-primary"
            disabled={!name.trim() || scopes.length === 0 || create.isPending}
            onClick={() => { step.run(() => create.mutateAsync({
              name: name.trim(),
              scopes,
              ...(allowlist.trim()
                ? { ip_allowlist: allowlist.split(',').map((x) => x.trim()).filter(Boolean) }
                : {}),
              ...(expiryDays ? { expires_in_days: Number(expiryDays) } : {}),
            })).catch(toastError); }}
          >
            {create.isPending ? 'Creating…' : 'Create key'}
          </button>
        </div>
      </div>
    </div>
  );
}
